'use client';

import { Button } from '@ahafood/ui/components/button';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import PersonalizeItem from './personalize-item';

import { useGetPersonalize } from '@/api/use-get-personalize';
import { useAuthStore } from '@/store';
import { ahafoodStore } from '@/store/use-ai-store';

import './personalize.css';

interface PersonalizeContainerProps {
  handleSuggestionClick?: (suggestion: string) => void;
  handleItemClick: (item: any) => void;
}

export default function PersonalizeContainer({
  handleSuggestionClick,
  handleItemClick,
}: PersonalizeContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [centerIndex, setCenterIndex] = useState(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuthStore();

  // Get store data
  const { address, city_id } = ahafoodStore();

  // Prepare API parameters
  const params = {
    customer_id: user?.cunstomerId ?? '',
    city_id: city_id ?? 'SGN',
    lat: address?.lat ?? 10.7769,
    lng: address?.lng ?? 106.7009,
    limit: 20,
  };

  // Fetch personalize data
  const {
    data: personalizeData,
    isLoading,
    error,
  } = useGetPersonalize(params, {
    enabled: !!user?.cunstomerId && !!city_id,
    retry: 1,
  });

  // Wrapper function to add tag before calling original handleItemClick
  const handlePersonalizeItemClick = useCallback(
    (item: any) => {
      // Add tag to the item before passing to handleItemClick
      const itemWithTag = { ...item, tag: 'for_you' };
      handleItemClick(itemWithTag);
    },
    [handleItemClick]
  );

  // Handle scroll to detect center item
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !personalizeData?.items?.length) return;

    const container = scrollRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const items = container.children;
    let closestIndex = 0;
    let minDistance = Infinity;

    // Tìm item gần center nhất
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(containerCenter - itemCenter);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    // Chỉ cập nhật nếu có thay đổi
    if (closestIndex !== centerIndex) {
      setCenterIndex(closestIndex);
    }
  }, [personalizeData?.items?.length, centerIndex]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Throttled scroll handler - giảm delay để responsive hơn
    const throttledScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(handleScroll, 16); // ~60fps
    };

    container.addEventListener('scroll', throttledScroll);

    // Initial call to set center index
    handleScroll();

    return () => {
      container.removeEventListener('scroll', throttledScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  if (isLoading) {
    return (
      <div className="w-full py-6">
        <h2 className="text-xl font-bold mb-4 px-4">Đề xuất cho bạn</h2>
        <div className="flex gap-4 px-4 overflow-hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="shrink-0 w-64 h-80 bg-neutral-20 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error ?? !personalizeData?.items?.length) {
    return null;
  }

  return (
    <div className="w-full pb-6 overflow-y-visible px-0 personalize-container flex-1">
      <div className="flex items-center justify-between pb-3">
        <h2 className="text-base lg:text-xl font-bold text-neutral-90">
          Món ngon cho bạn
        </h2>
        <Button
          variant="link"
          className="text-primary-50 -mr-4 md:mr-0 underline text-sm md:text-base font-thin hover:text-primary-60"
          onClick={() => handleSuggestionClick?.('Các món dành cho tôi')}
        >
          Hỏi Amori thêm
        </Button>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-9 md:gap-6 overflow-x-auto scroll-smooth overflow-y-hidden personalize-scroll p-4 -mx-4 md:-mx-6"
      >
        {personalizeData.items.map((item, index) => (
          <PersonalizeItem
            handleItemClick={handlePersonalizeItemClick}
            handleSuggestionClick={handleSuggestionClick}
            key={item._id}
            item={item}
            isCenter={index === centerIndex}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}
